import { useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';

import { Box, ButtonBase, FormControlLabel, Typography } from '@/shared/ui';

import type { CatalogItem } from '@/entities/catalog-item';
import Checkbox from '@/shared/ui/primitives/Checkbox';
import {
  ADDITIONAL_ITEM,
  additionalColumnsList,
  defaultColumnsList,
  moreColumnsList
} from '../../../constants';
import type { DisplayColumn } from '../../../constants';
import ColumnToggleTile, { COLUMN_TILE_MIN_WIDTH } from './ColumnToggleTile';

import type { Table } from '@tanstack/react-table';

const selectAllButtonSx = { color: 'gx.highlight', px: 0.2, fontSize: '10px' };
// Shared header row for the column groups. The fixed minHeight keeps every
// group's header the same height whether or not it has a "Select all" button,
// so the tile rows below them line up (the Additional group has no button).
const columnGroupHeaderSx = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 2,
  minHeight: 18
};
type CatalogTableColumns = ReturnType<Table<CatalogItem>['getAllColumns']>;

const findColumn = (accessorKey: string, tanStackColumns: CatalogTableColumns) =>
  tanStackColumns.find(({ id }) => id === accessorKey);

const renderColumnTiles = (columnsList: DisplayColumn[], tanStackColumns: CatalogTableColumns) =>
  columnsList.map(({ displayLabel, accessorKey }) => {
    const column = findColumn(accessorKey, tanStackColumns);
    if (!column) return null;

    return (
      <ColumnToggleTile
        key={accessorKey}
        checked={column.getIsVisible()}
        label={displayLabel}
        onToggle={() => column.toggleVisibility()}
      />
    );
  });

const getGroupToggleState = (
  columnsList: DisplayColumn[],
  tanStackColumns: CatalogTableColumns
) => {
  const columns = columnsList
    .map(({ accessorKey }) => findColumn(accessorKey, tanStackColumns))
    .filter((column): column is NonNullable<typeof column> => Boolean(column));
  const allSelected = columns.length > 0 && columns.every(column => column.getIsVisible());

  return {
    allSelected,
    toggleAll: () => {
      columns.forEach(column => column.toggleVisibility(!allSelected));
    }
  };
};

const DisplaySettingsPanel = ({
  expandedData,
  setExpandedData,
  table,
  isForwardCurves,
  showDiscontinued,
  setShowDiscontinued
}: {
  showDiscontinued: boolean;
  setShowDiscontinued: Dispatch<SetStateAction<boolean>>;
  expandedData: boolean;
  isForwardCurves: boolean;
  setExpandedData: (newValue: boolean) => void;
  table: Table<CatalogItem>;
}) => {
  const tanStackColumns = table.getAllColumns();
  // TODO: wire "Forward periods inline" to the real expand-rows-to-show-periods
  // behavior once that ticket lands. Purely visual/local state until then.
  const [forwardPeriodsInline, setForwardPeriodsInline] = useState(true);
  const handleChangeExpandedData = (allItems: boolean) => {
    if (isForwardCurves || allItems === expandedData) return;
    setExpandedData(allItems);
    const columnIdsToChangeVisibility = new Set([
      'TimeRef',
      'PeriodType',
      'Period',
      ADDITIONAL_ITEM
    ]);
    // Flip each column (no explicit target) rather than setting it to `allItems`:
    // the period columns (TimeRef/PeriodType/Period) start hidden while
    // AdditionalItem starts visible, so they move in OPPOSITE directions on every
    // mode switch. These four columns are changed only here, and the guard above
    // skips the no-op case, so flipping keeps each one correctly in sync with the
    // view mode — setting them all to `allItems` would invert AdditionalItem.
    tanStackColumns
      .filter(column => columnIdsToChangeVisibility.has(column.id))
      .forEach(column => column.toggleVisibility());
  };

  const defaultColumnsToggle = getGroupToggleState(defaultColumnsList, tanStackColumns);
  const moreColumnsToggle = getGroupToggleState(moreColumnsList, tanStackColumns);

  return (
    <Box
      data-testid='display-settings-panel'
      sx={{
        display: 'flex',
        gap: 3,
        p: 2,
        minWidth: 280,
        width: '100%',
        overflowX: 'auto',
        backgroundColor: 'gx.tileDark',
        borderBottom: '1px solid',
        borderColor: 'gx.border'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          flexShrink: 0,
          gap: 1.5,
          width: 220
        }}
      >
        {!isForwardCurves && (
          <>
            <Box
              sx={{ display: 'flex', height: 26, border: '0.5px solid', borderColor: 'gx.border' }}
            >
              <ButtonBase
                aria-pressed={!expandedData}
                sx={{
                  flex: 1,
                  fontSize: '10px',
                  fontWeight: 700,
                  backgroundColor: !expandedData ? 'gx.response' : 'gx.tileDark',
                  color: 'gx.text'
                }}
                onClick={() => handleChangeExpandedData(false)}
              >
                1 per code
              </ButtonBase>
              <ButtonBase
                aria-pressed={expandedData}
                sx={{
                  flex: 1,
                  fontSize: '10px',
                  borderLeft: '0.5px solid',
                  borderColor: 'gx.border',
                  backgroundColor: expandedData ? 'gx.response' : 'gx.tileDark',
                  color: expandedData ? 'gx.text' : 'gx.textDisabled'
                }}
                onClick={() => handleChangeExpandedData(true)}
              >
                All items
              </ButtonBase>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={forwardPeriodsInline}
                  onChange={() => setForwardPeriodsInline(checked => !checked)}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontSize: '10px' }}>Forward periods inline</Typography>
                  <Typography
                    color='info'
                    component='div'
                    sx={{ fontSize: '9px' }}
                  >
                    Expand rows to see periods
                  </Typography>
                </Box>
              }
              sx={{ m: 0, alignItems: 'flex-start' }}
            />
          </>
        )}
        <FormControlLabel
          control={
            <Checkbox
              checked={showDiscontinued}
              onChange={() => {
                setShowDiscontinued(!showDiscontinued);
              }}
            />
          }
          label={
            <Typography sx={{ fontSize: '10px', color: 'table.infoRowText' }}>
              Show discontinued items
            </Typography>
          }
          sx={{ m: 0 }}
        />
      </Box>

      <Box
        data-testid='display-settings-default-columns'
        sx={{ display: 'flex', flexDirection: 'column', flexGrow: 2, gap: 1 }}
      >
        <Box sx={columnGroupHeaderSx}>
          <Typography
            color='info'
            component='div'
            sx={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px' }}
          >
            Default Columns
          </Typography>
          <ButtonBase
            sx={selectAllButtonSx}
            onClick={defaultColumnsToggle.toggleAll}
          >
            {defaultColumnsToggle.allSelected ? 'Clear all' : 'Select all'}
          </ButtonBase>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(2, minmax(${COLUMN_TILE_MIN_WIDTH}px, 1fr))`,
            gap: 1
          }}
        >
          {renderColumnTiles(defaultColumnsList, tanStackColumns)}
        </Box>
      </Box>

      {!isForwardCurves && (
        <Box
          data-testid='display-settings-additional-columns'
          sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 1 }}
        >
          <Box sx={columnGroupHeaderSx}>
            <Typography
              color='info'
              component='div'
              sx={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px' }}
            >
              Additional Columns
            </Typography>
          </Box>
          {renderColumnTiles(additionalColumnsList, tanStackColumns)}
        </Box>
      )}

      <Box
        data-testid='display-settings-more-columns'
        sx={{ display: 'flex', flexDirection: 'column', flexGrow: 3, gap: 1 }}
      >
        <Box sx={columnGroupHeaderSx}>
          <Typography
            color='info'
            component='div'
            sx={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px' }}
          >
            More Columns
          </Typography>
          <ButtonBase
            sx={selectAllButtonSx}
            onClick={moreColumnsToggle.toggleAll}
          >
            {moreColumnsToggle.allSelected ? 'Clear all' : 'Select all'}
          </ButtonBase>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(3, minmax(${COLUMN_TILE_MIN_WIDTH}px, 1fr))`,
            gap: 1
          }}
        >
          {renderColumnTiles(moreColumnsList, tanStackColumns)}
        </Box>
      </Box>
    </Box>
  );
};

export default DisplaySettingsPanel;
